/**
 * FinalExercise.jsx
 * Formulario de reflexión estructurada para reclamar la insignia.
 * Usa `useFinalExerciseDraft` para persistencia en localStorage.
 *
 * ESTADOS EXCLUYENTES:
 *   Estado 1 — formulario visible (! draft.submitted)
 *   Estado 2 — ejercicio completado, badge no reclamado (draft.submitted && ! draft.claimResult)
 *   Estado 3 — badge emitido (draft.claimResult != null)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import { useFinalExerciseDraft } from '../../hooks/useFinalExerciseDraft';
import BadgeClaimFlow from './BadgeClaimFlow';
import { Award, CheckCircle2, Loader, ExternalLink } from 'lucide-react';

export default function FinalExercise({ program, isAuthenticated, user }) {
  const navigate = useNavigate();
  const { draft, updateDraft, clearDraft } = useFinalExerciseDraft(program.slug);
  const [submitError, setSubmitError] = useState('');
  const [checkingBadge, setCheckingBadge] = useState(false);
  const didCheckRef = useRef(false);

  // ── Al montar: si hay sesión y aún no hay claim local, consultar backend.
  //    Cubre dos casos:
  //    1. El usuario ya envió el ejercicio en ESTA PC → restaurar State 3
  //    2. El usuario viene de OTRA PC (sin draft) → detectar badge existente
  //       antes de que rellene el ejercicio de nuevo.
  //    Regla de negocio: el programa puede repetirse; la insignia no.
  useEffect(() => {
    if (!isAuthenticated || draft.claimResult || didCheckRef.current) return;
    didCheckRef.current = true;
    setCheckingBadge(true);
    apiClient.get(`/badges/by-program/${program.slug}`)
      .then(res => {
        if (res.data?.hasBadge && res.data?.badge) {
          updateDraft({ claimResult: res.data.badge });
        }
      })
      .catch(() => { /* badge no existe aún — ignorar */ })
      .finally(() => setCheckingBadge(false));
  }, [isAuthenticated, draft.claimResult, program.slug, updateDraft]);

  if (checkingBadge) {
    return (
      <div className="flex items-center gap-3 py-8 text-slate-500">
        <Loader className="w-5 h-5 animate-spin text-cyan-500" />
        <span className="text-sm">Verificando insignia...</span>
      </div>
    );
  }

  /* ───── Estado 3: ya reclamado — bloque corto, badge vive en Dashboard > Certificados ───── */
  if (draft.claimResult) {
    const { verificationId } = draft.claimResult;
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-800">Ya reclamaste esta insignia con tu cuenta actual.</p>
          <p className="text-xs text-emerald-600 mt-0.5">Tu certificado digital está disponible en tu panel privado.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
          <button
            onClick={() => navigate(`/home/certificates/${verificationId}`)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg text-xs transition-colors"
          >
            <Award className="w-3.5 h-3.5" />
            Ver mi certificado
          </button>
          <a
            href={`/badge/${verificationId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-100 font-semibold rounded-lg text-xs transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Ver enlace verificable
          </a>
        </div>
      </div>
    );
  }

  /* ───── Fase: enviado (esperando reclamar) ───── */
  if (draft.submitted) {
    const feedbackText = program.feedbackByOption?.[draft.q1];
    return (
      <div className="bg-white rounded-2xl border border-cyan-200 shadow-sm overflow-hidden">
        {/* Feedback pedagógico */}
        {feedbackText && (
          <div className="px-6 sm:px-10 pt-8 pb-4">
            <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-lg mb-6">
              <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-cyan-800 mb-1">Tu reflexión fue registrada — perspectiva del programa:</p>
                <p className="text-sm text-cyan-900 leading-relaxed">{feedbackText}</p>
              </div>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Ejercicio completado</h3>
            <p className="text-sm text-slate-600 mb-4">Ya puedes reclamar tu insignia digital.</p>
          </div>
        )}
        <div className="px-6 sm:px-10 pb-8">
          <BadgeClaimFlow
            program={program}
            answers={{ q1: draft.q1, q2: draft.q2, q3: draft.q3 }}
            isAuthenticated={isAuthenticated}
            onClaimed={(badge) => updateDraft({ claimResult: badge })}
            onClearDraft={clearDraft}
          />
        </div>
      </div>
    );
  }

  /* ───── Fase: formulario ───── */
  const q2Len = (draft.q2 || '').length;
  const q3Len = (draft.q3 || '').length;
  const minCharsQ2 = program.finalQuestions?.find(q => q.id === 'q2')?.minChars || 100;
  const minCharsQ3 = program.finalQuestions?.find(q => q.id === 'q3')?.minChars || 100;
  const canSubmit = draft.q1 && q2Len >= minCharsQ2 && q3Len >= minCharsQ3;

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) {
      setSubmitError('Completa todos los campos requeridos.');
      return;
    }
    setSubmitError('');
    updateDraft({ submitted: true, feedbackShown: true });
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="h-1.5 w-full bg-gradient-to-r from-cyan-500 to-blue-600" />
      <div className="px-6 sm:px-10 pt-8 pb-4">
        <div className="flex items-center gap-2 mb-2">
          <Award className="w-5 h-5 text-cyan-600" />
          <span className="text-xs font-semibold uppercase tracking-wider text-cyan-600">Ejercicio final</span>
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
          {program.badgeLabel || 'Insignia de completación'}
        </h2>
        {program.badgeSubtitle && (
          <p className="text-sm text-slate-600 mb-4">{program.badgeSubtitle}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="px-6 sm:px-10 pb-10 space-y-8">
        {/* Escenario */}
        {program.finalScenario && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-sm font-semibold text-slate-700 mb-1">Escenario:</p>
            <p className="text-sm text-slate-700 leading-relaxed">{program.finalScenario}</p>
          </div>
        )}

        {/* Q1 — opción múltiple */}
        {program.finalQuestions && (
          <>
            <div className="space-y-3">
              <label className="block text-base font-semibold text-slate-900">
                {program.finalQuestions.find(q => q.id === 'q1')?.text || '¿Cuál sería tu primer movimiento?'}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="space-y-2">
                {(program.finalOptions || []).map(opt => (
                  <label
                    key={opt.key}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      draft.q1 === opt.key
                        ? 'border-cyan-500 bg-cyan-50'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="q1"
                      value={opt.key}
                      checked={draft.q1 === opt.key}
                      onChange={() => updateDraft({ q1: opt.key })}
                      className="accent-cyan-600"
                    />
                    <span className="text-sm text-slate-800">
                      <strong>{opt.key}.</strong> {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Q2 — texto largo */}
            <div className="space-y-2">
              <label className="block text-base font-semibold text-slate-900">
                {program.finalQuestions.find(q => q.id === 'q2')?.text || '¿Cuál es el mayor riesgo?'}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={draft.q2 || ''}
                onChange={e => updateDraft({ q2: e.target.value })}
                rows={5}
                placeholder="Escribe tu respuesta aquí..."
                className={`w-full rounded-lg border px-4 py-3 text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${
                  q2Len > 0 && q2Len < minCharsQ2 ? 'border-amber-300' : 'border-slate-300'
                }`}
              />
              <p className={`text-xs text-right ${
                q2Len >= minCharsQ2 ? 'text-emerald-600' : 'text-slate-400'
              }`}>
                {q2Len} / {minCharsQ2} mínimo
              </p>
            </div>

            {/* Q3 — texto largo */}
            <div className="space-y-2">
              <label className="block text-base font-semibold text-slate-900">
                {program.finalQuestions.find(q => q.id === 'q3')?.text || '¿Qué harías en las próximas 48h?'}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={draft.q3 || ''}
                onChange={e => updateDraft({ q3: e.target.value })}
                rows={5}
                placeholder="Escribe tu respuesta aquí..."
                className={`w-full rounded-lg border px-4 py-3 text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${
                  q3Len > 0 && q3Len < minCharsQ3 ? 'border-amber-300' : 'border-slate-300'
                }`}
              />
              <p className={`text-xs text-right ${
                q3Len >= minCharsQ3 ? 'text-emerald-600' : 'text-slate-400'
              }`}>
                {q3Len} / {minCharsQ3} mínimo
              </p>
            </div>
          </>
        )}

        {submitError && (
          <p className="text-sm text-red-600">{submitError}</p>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all ${
            canSubmit
              ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-md hover:shadow-lg'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          <Award className="w-4 h-4" />
          Enviar ejercicio y ver feedback
        </button>
      </form>
    </div>
  );
}
